import { useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import DashStatCard from '../../components/ui/DashStatCard'
import LineChart    from '../../components/ui/LineChart'
import DashIcon     from '../../components/ui/DashIcon'
import StatusBadge  from '../../components/ui/StatusBadge'
import { deliveriesAPI } from '../../api/deliveries'
import { analyticsAPI }  from '../../api/analytics'
import { usersAPI }      from '../../api/users'
import { useAuthStore }  from '../../store/authStore'
import type { Delivery, PaginatedResponse } from '../../types'

const TUNISIA_PATH = 'M 230 30 L 280 35 L 310 60 L 305 95 L 340 110 L 360 145 L 360 195 L 365 260 L 355 300 L 340 340 L 360 380 L 410 420 L 395 460 L 350 485 L 305 490 L 280 470 L 250 440 L 210 410 L 180 380 L 140 340 L 120 290 L 130 240 L 155 200 L 170 160 L 180 120 L 200 80 L 215 50 Z'

const COURIER_PTS = [
  { cx: 280, cy: 100 }, { cx: 310, cy: 180 }, { cx: 330, cy: 260 },
  { cx: 290, cy: 330 }, { cx: 250, cy: 155 },
]

const MAP_CITIES = [
  { x: 280, y: 90,  n: 'Tunis',  major: true },
  { x: 330, y: 280, n: 'Sfax',   major: true },
  { x: 310, y: 180, n: 'Sousse', major: false },
  { x: 320, y: 120, n: 'Nabeul', major: false },
]

const FALLBACK_LABELS   = ['9h','10h','11h','12h','13h','14h','15h','16h','17h','18h']
const FALLBACK_EARNINGS = [12, 24, 18, 38, 45, 52, 41, 67, 72, 84]

const PERF_KEYS = [
  { label: 'Taux de livraison', key: 'deliveryRate',   color: 'var(--green-500)', fallback: 96 },
  { label: "À l'heure",         key: 'onTimeRate',     color: 'var(--blue)',      fallback: 91 },
  { label: 'Note client',       key: 'ratingScore',    color: 'var(--amber)',     fallback: 98 },
  { label: 'Acceptation',       key: 'acceptanceRate', color: '#7C5CD3',          fallback: 88 },
]

const fmtDT = (n: number) => n.toLocaleString('fr-TN', { minimumFractionDigits: 3 }) + ' DT'

export default function LivreurDashboard() {
  const navigate = useNavigate()
  const { user }  = useAuthStore()
  const [isOnline,  setIsOnline]  = useState(false)
  const [toggling,  setToggling]  = useState(false)

  const { data: activeData, isLoading: loadingActive } = useQuery<PaginatedResponse<Delivery>>({
    queryKey: ['dashboard-active'],
    queryFn: async () => {
      const res = await deliveriesAPI.getAll({ status: 'in_transit', limit: 4 })
      return res
    },
  })

  const { data: stats } = useQuery<Record<string, number>>({
    queryKey: ['livreur-stats-day'],
    queryFn: async () => {
      const res = await analyticsAPI.getLivreurStats('day')
      return res.data as Record<string, number>
    },
  })

  const { data: timelineRaw } = useQuery<Record<string, unknown>>({
    queryKey: ['livreur-timeline-day'],
    queryFn: async () => {
      const res = await analyticsAPI.getTimeline('day')
      return res as unknown as Record<string, unknown>
    },
  })

  const active    = activeData?.data ?? []
  const firstName = user?.name?.split(' ')[0] ?? 'Livreur'

  const chartEarnings = (timelineRaw?.values ?? timelineRaw?.earnings ?? timelineRaw?.data ?? FALLBACK_EARNINGS) as number[]
  const chartLabels   = (timelineRaw?.labels ?? FALLBACK_LABELS) as string[]

  const todayGains      = stats?.todayEarnings ?? stats?.revenue ?? 0
  const totalToday      = stats?.todayDeliveries ?? stats?.delivered ?? 0
  const rating          = stats?.rating ?? 4.9
  const distanceKm      = stats?.distanceKm ?? 62

  const toggleOnline = async () => {
    setToggling(true)
    try {
      await usersAPI.setAvailability(!isOnline)
      setIsOnline(prev => !prev)
      toast.success(!isOnline ? 'Vous êtes en ligne' : 'Vous êtes hors ligne')
    } catch {
      toast.error('Erreur lors de la mise à jour du statut')
    } finally {
      setToggling(false)
    }
  }

  const now = new Date()
  const dayLabel = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Bonjour {firstName} 👋</h1>
          <p style={{ textTransform: 'capitalize' }}>
            {dayLabel}
            {isOnline && <> · <span className="pulse-dot" /> En service</>}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="clock" size={14} /> Pause</button>
          <button
            className={isOnline ? 'btn btn-ghost' : 'btn btn-green'}
            onClick={toggleOnline}
            disabled={toggling}
          >
            <DashIcon name="zap" size={14} /> {isOnline ? 'Passer hors ligne' : 'Aller en ligne'}
          </button>
        </div>
      </div>

      <div className="stat-grid stat-grid-4" style={{ marginBottom: 18 }}>
        <DashStatCard
          label="Gains aujourd'hui" value={fmtDT(todayGains)}
          trend="+23% vs hier" icon="wallet" accent="green"
          spark={[10,14,22,18,32,28,40,52,48,84]}
        />
        <DashStatCard
          label="Livraisons" value={`${totalToday} / ${(activeData?.total ?? 0) + totalToday}`}
          trend={`${activeData?.total ?? 0} restantes`} icon="package"
          spark={[2,4,3,6,8,9,11,12,13,totalToday]}
        />
        <DashStatCard
          label="Note moyenne" value={`${rating} ★`}
          trend="+0.1 cette semaine" icon="star"
        />
        <DashStatCard
          label="Distance" value={`${distanceKm} km`}
          trend={`${activeData?.total ?? 0} livraisons à venir`} icon="truck"
        />
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1fr 360px', gap: 18, marginBottom: 18 } as CSSProperties}>
        <div className="card chart-card">
          <div className="chart-head">
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>
                Gains horaires
              </div>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy-900)', marginTop: 2 }}>
                {fmtDT(todayGains)}
              </div>
            </div>
            <div className="seg">
              <button>Hier</button>
              <button className="is-active">Aujourd'hui</button>
              <button>Semaine</button>
            </div>
          </div>
          <div className="chart-body">
            <LineChart series={[{ data: chartEarnings }]} labels={chartLabels} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Ma zone</h3>
            <span className={`badge ${isOnline ? 'badge-green' : ''}`} style={!isOnline ? { background: 'var(--navy-50)', color: 'var(--muted)' } : {}}>
              {isOnline && <span className="pulse-dot" />} {isOnline ? 'Active' : 'Hors ligne'}
            </span>
          </div>
          <div className="map-mini">
            <svg viewBox="0 0 500 520" preserveAspectRatio="xMidYMid meet">
              <path d={TUNISIA_PATH} fill="rgba(124,179,66,.06)" stroke="rgba(124,179,66,.4)" strokeWidth="1.2" strokeDasharray="3 3" />
              {MAP_CITIES.map((c, i) => (
                <g key={i}>
                  {c.major && (
                    <circle cx={c.x} cy={c.y} r="10" fill="none" stroke="#7CB342" strokeWidth="1.5" opacity=".6">
                      <animate attributeName="r" values="5;18;5" dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values=".7;0;.7" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle cx={c.x} cy={c.y} r={c.major ? 5 : 3.5} fill="#fff" stroke="#7CB342" strokeWidth="2" />
                  <text x={c.x + (c.major ? 10 : 7)} y={c.y + 4}
                    fill={c.major ? '#fff' : 'rgba(255,255,255,.7)'}
                    fontSize="11" fontFamily="JetBrains Mono" fontWeight={c.major ? 700 : 500}>{c.n}
                  </text>
                </g>
              ))}
              {COURIER_PTS.map((p, i) => (
                <g key={i} transform={`translate(${p.cx},${p.cy})`}>
                  <circle r="7" fill="none" stroke="#7CB342" strokeWidth="1.5" opacity=".4">
                    <animate attributeName="r" values="4;12;4" dur="2s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
                  </circle>
                  <circle r="4" fill="#7CB342" stroke="#fff" strokeWidth="1.5" />
                </g>
              ))}
            </svg>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: '.82rem' }}>
            <div>
              <b style={{ display: 'block', color: 'var(--navy-900)' }}>Tunis Centre</b>
              <span style={{ color: 'var(--muted)' }}>3.2 km de rayon</span>
            </div>
            <button className="btn btn-sm btn-ghost">Changer</button>
          </div>
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1.4fr 1fr', gap: 18 } as CSSProperties}>
        <div className="card">
          <div className="card-head">
            <h3>Livraisons en cours</h3>
            <button className="btn btn-sm btn-ghost" onClick={() => navigate('/livreur/colis')}>Voir tout →</button>
          </div>

          {loadingActive ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 24, fontSize: '.84rem' }}>Chargement…</div>
          ) : active.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 32, fontSize: '.84rem' }}>
              Aucune livraison en cours.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {active.map((o, i) => (
                <div
                  key={o._id}
                  style={{
                    display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 14,
                    alignItems: 'center', padding: '12px 14px',
                    border: '1px solid var(--line)', borderRadius: 12,
                    background: i === 0 ? 'linear-gradient(90deg, rgba(124,179,66,.05), transparent)' : '#fff',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, display: 'grid', placeItems: 'center',
                    background: i === 0 ? 'var(--green-500)' : 'var(--navy-50)',
                    color: i === 0 ? '#fff' : 'var(--navy-700)',
                    fontWeight: 700, fontSize: '.85rem', fontFamily: 'Space Grotesk',
                  }}>{i + 1}</div>
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono" style={{ color: 'var(--navy-900)', fontWeight: 600, fontSize: '.85rem' }}>
                        {o.trackingCode}
                      </span>
                      <StatusBadge status={o.status} />
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: '.78rem', marginTop: 2 }}>
                      {o.dropoff.address.split(',')[0]}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="mono" style={{ color: 'var(--navy-900)', fontWeight: 600, fontSize: '.88rem' }}>
                      {o.price?.base ?? 0} DT
                    </div>
                    <div style={{ color: 'var(--green-700)', fontSize: '.72rem', fontWeight: 600 }}>
                      +{o.price?.livreurAmount ?? 0} DT
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate(`/livreur/colis/${o._id}`)}
                  >Détails</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Performance</h3>
            <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Cette semaine</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {PERF_KEYS.map((p, i) => {
              const val = stats?.[p.key] ?? p.fallback
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.84rem', marginBottom: 6 }}>
                    <span style={{ color: 'var(--navy-800)', fontWeight: 600 }}>{p.label}</span>
                    <span className="mono" style={{ color: 'var(--navy-900)', fontWeight: 700 }}>{val}%</span>
                  </div>
                  <div className="bar"><i style={{ width: val + '%', background: p.color }} /></div>
                </div>
              )
            })}
            <div style={{
              marginTop: 6, padding: 14, background: 'var(--green-50)',
              borderRadius: 10, fontSize: '.84rem', color: 'var(--green-700)',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <DashIcon name="star" size={16} />
              <span><b>Top 5%</b> des livreurs de Tunis cette semaine. Continue!</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
